/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Document Chunk Previewer Drawer (Task 10.3.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useEffect } from 'react';
import { useKnowledgeStore } from '../../stores/useKnowledgeStore';
import { useI18n } from '../../hooks/useI18n';
import {
  X,
  Layers,
  Copy,
  Check,
  Search,
  FileText,
  FileCode,
  FileSpreadsheet,
  File,
  Hash,
} from 'lucide-react';

export const ChunkPreviewerDrawer: React.FC = () => {
  const { t } = useI18n();
  const { selectedPreviewDoc, closeChunkPreview } = useKnowledgeStore();
  const [searchChunkQuery, setSearchChunkQuery] = useState('');
  const [copiedChunkId, setCopiedChunkId] = useState<string | null>(null);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedPreviewDoc) {
        closeChunkPreview();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPreviewDoc, closeChunkPreview]);

  if (!selectedPreviewDoc) return null;

  const getDocIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return FileText;
      case 'docx':
        return File;
      case 'csv':
        return FileSpreadsheet;
      case 'markdown':
        return FileCode;
      default:
        return FileText;
    }
  };

  const DocIcon = getDocIcon(selectedPreviewDoc.fileType);

  const handleCopyChunk = async (chunkId: string, content: string) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(content);
        setCopiedChunkId(chunkId);
        setTimeout(() => setCopiedChunkId(null), 2000);
      }
    } catch {
      // Ignored
    }
  };

  const filteredChunks = selectedPreviewDoc.sampleChunks.filter((chunk) => {
    if (!searchChunkQuery.trim()) return true;
    const q = searchChunkQuery.toLowerCase();
    return (
      chunk.content.toLowerCase().includes(q) ||
      (chunk.metadata.heading && chunk.metadata.heading.toLowerCase().includes(q))
    );
  });

  return (
    <div className="fixed inset-0 z-50 flex justify-end animate-in fade-in duration-200">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={closeChunkPreview}
      />

      {/* Slide-over Drawer Panel */}
      <div className="relative z-10 flex h-full w-full max-w-xl flex-col border-l border-surface-border bg-surface shadow-2xl animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-surface-border p-4 bg-surface-elevated/40">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary flex-shrink-0">
              <DocIcon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-sm text-foreground line-clamp-1">
                {selectedPreviewDoc.title}
              </h3>
              <p className="text-[11px] text-foreground-muted">
                {t('knowledge.chunksCount', { count: selectedPreviewDoc.chunkCount })} • {selectedPreviewDoc.formattedSize} •{' '}
                {selectedPreviewDoc.embeddingDimensions}d Vectorize
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={closeChunkPreview}
            className="p-1.5 rounded-xl text-foreground-muted hover:text-foreground hover:bg-muted transition-colors"
            title={t('knowledge.closeDrawer')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search Inside Chunks Bar */}
        <div className="p-3 border-b border-surface-border bg-surface/50">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
            <input
              type="text"
              value={searchChunkQuery}
              onChange={(e) => setSearchChunkQuery(e.target.value)}
              placeholder={t('knowledge.searchChunksPlaceholder')}
              className="w-full pl-9 pr-3 py-1.5 rounded-xl border border-surface-border bg-surface text-xs text-foreground placeholder:text-foreground-muted/60 focus:outline-none focus:ring-1 focus:ring-primary shadow-inner"
            />
          </div>
        </div>

        {/* Chunks List Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4 scrollbar-thin">
          <div className="flex items-center justify-between text-[11px] text-foreground-muted px-1">
            <span className="font-bold uppercase tracking-wider">
              {t('knowledge.semanticChunksList', { count: filteredChunks.length })} / {selectedPreviewDoc.chunkCount}
            </span>
            <span className="font-mono text-primary font-semibold">
              Model: {selectedPreviewDoc.embeddingModel}
            </span>
          </div>

          {filteredChunks.length > 0 ? (
            <div className="space-y-3">
              {filteredChunks.map((chunk, idx) => (
                <div
                  key={chunk.id || idx}
                  className="p-4 rounded-2xl border border-surface-border bg-surface-elevated/40 hover:bg-surface-elevated/70 transition-colors space-y-2.5 shadow-sm"
                >
                  {/* Chunk Metadata Banner */}
                  <div className="flex items-center justify-between text-[11px]">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1 font-mono font-bold text-accent px-2 py-0.5 rounded-md bg-accent/10 border border-accent/20">
                        <Hash className="w-3 h-3" />
                        {t('knowledge.chunkNum', { num: chunk.chunkIndex + 1 })}
                      </span>
                      {chunk.metadata.heading && (
                        <span className="font-medium text-foreground truncate max-w-[200px]">
                          {chunk.metadata.heading}
                        </span>
                      )}
                      {chunk.metadata.page && (
                        <span className="text-foreground-muted">{t('knowledge.pageLabel', { page: chunk.metadata.page })}</span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-foreground-muted">
                        {t('knowledge.tokensCount', { count: chunk.tokenCount })}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCopyChunk(chunk.id, chunk.content)}
                        className="p-1 rounded-md text-foreground-muted hover:text-foreground hover:bg-muted transition-colors"
                        title={t('knowledge.copyChunkTooltip')}
                      >
                        {copiedChunkId === chunk.id ? (
                          <Check className="w-3.5 h-3.5 text-emerald-500" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Chunk Text Body */}
                  <div className="p-3 rounded-xl bg-surface border border-surface-border text-xs text-foreground leading-relaxed font-sans selection:bg-primary/20 whitespace-pre-wrap">
                    {chunk.content}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-foreground-muted space-y-2">
              <Layers className="w-8 h-8 mx-auto text-foreground-muted/40" />
              <p className="text-xs">{t('knowledge.noChunksMatch')}</p>
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="p-4 border-t border-surface-border bg-surface-elevated/40 flex items-center justify-between text-xs text-foreground-muted">
          <span className="font-mono truncate max-w-xs">
            R2: {selectedPreviewDoc.r2StorageKey}
          </span>
          <span className="font-bold text-primary font-mono">
            {selectedPreviewDoc.vectorizeIndexName}
          </span>
        </div>
      </div>
    </div>
  );
};
